import { Chip, Button } from '@heroui/react';
import { AlertTriangle, Scaling, Crosshair } from 'lucide-react';
import { useUpdatePageScale } from '../../api/knowledge';

const COMMON_SCALES = [
  { label: '1/4" = 1\'', ratio: 48, unit: 'ft' },
  { label: '1/8" = 1\'', ratio: 96, unit: 'ft' },
  { label: '3/16" = 1\'', ratio: 64, unit: 'ft' },
  { label: '3/8" = 1\'', ratio: 32, unit: 'ft' },
  { label: '1/2" = 1\'', ratio: 24, unit: 'ft' },
  { label: '1" = 1\'', ratio: 12, unit: 'ft' },
  { label: '1:50', ratio: 50, unit: 'm' },
  { label: '1:100', ratio: 100, unit: 'm' },
];

const DPI = 300;

function ratioToPixelsPerUnit(ratio: number, unit: string): number {
  if (unit === 'ft') return (DPI * 12) / ratio;
  return (DPI * 39.37) / ratio;
}

function isScaleMatch(currentPpu: number, ratio: number, unit: string): boolean {
  const expected = ratioToPixelsPerUnit(ratio, unit);
  return Math.abs(currentPpu - expected) < 0.5;
}

interface ScalePillsBarProps {
  pageId: string;
  currentScale: number | null;
  currentUnit: string;
}

export default function ScalePillsBar({ pageId, currentScale, currentUnit }: ScalePillsBarProps) {
  const updateScale = useUpdatePageScale();
  const hasScale = currentScale != null && currentScale > 0;

  const handleApplyScale = (ratio: number, unit: string) => {
    const ppu = ratioToPixelsPerUnit(ratio, unit);
    updateScale.mutate({ pageId, pixelsPerUnit: ppu, unit });
  };

  const handleOpenCalibrate = () => {
    window.dispatchEvent(new CustomEvent('calibrate-line'));
  };

  const handleOpenKnownScale = () => {
    window.dispatchEvent(new CustomEvent('open-known-scale'));
  };

  return (
    <div
      className="flex items-center gap-2 px-3 py-1.5 border-b overflow-x-auto"
      style={{
        backgroundColor: hasScale ? 'var(--bg-surface)' : 'color-mix(in srgb, var(--yellow) 6%, var(--bg-surface))',
        borderColor: hasScale ? 'var(--border)' : 'color-mix(in srgb, var(--yellow) 20%, var(--border))',
      }}
    >
      {/* Warning indicator when no scale */}
      {!hasScale && (
        <Chip
          size="sm"
          variant="flat"
          startContent={<AlertTriangle size={12} />}
          className="text-[10px] h-5 flex-shrink-0 font-medium"
          style={{
            backgroundColor: 'color-mix(in srgb, var(--yellow) 15%, transparent)',
            color: 'var(--yellow)',
          }}
        >
          No scale
        </Chip>
      )}

      {/* Active scale indicator */}
      {hasScale && (
        <Chip
          size="sm"
          variant="flat"
          startContent={<Scaling size={11} />}
          className="text-[10px] h-5 flex-shrink-0 font-medium"
          style={{
            backgroundColor: 'color-mix(in srgb, var(--green) 15%, transparent)',
            color: 'var(--green)',
          }}
        >
          {currentScale!.toFixed(1)} px/{currentUnit}
        </Chip>
      )}

      {/* Divider */}
      <div
        className="w-px h-4 flex-shrink-0"
        style={{ backgroundColor: 'var(--border)' }}
      />

      {/* Scale pills */}
      {COMMON_SCALES.map((s) => {
        const isActive = hasScale && isScaleMatch(currentScale!, s.ratio, s.unit);
        return (
          <button
            key={s.label}
            onClick={() => handleApplyScale(s.ratio, s.unit)}
            disabled={updateScale.isPending}
            className="flex-shrink-0 px-2 py-0.5 rounded-full text-[10px] font-medium border transition-all duration-150 cursor-pointer hover:brightness-110 disabled:opacity-50"
            style={{
              backgroundColor: isActive
                ? 'color-mix(in srgb, var(--accent) 20%, transparent)'
                : 'var(--bg-panel)',
              borderColor: isActive
                ? 'var(--accent)'
                : 'var(--border)',
              color: isActive
                ? 'var(--accent)'
                : 'var(--text-secondary)',
            }}
          >
            {s.label}
          </button>
        );
      })}

      {/* Divider */}
      <div
        className="w-px h-4 flex-shrink-0"
        style={{ backgroundColor: 'var(--border)' }}
      />

      {/* Calibrate button */}
      <Button
        size="sm"
        variant="light"
        startContent={<Crosshair size={12} />}
        onPress={handleOpenCalibrate}
        className="h-5 min-w-0 px-2 text-[10px] flex-shrink-0"
        style={{ color: 'var(--text-muted)' }}
      >
        Calibrate
      </Button>

      {/* More scales button */}
      <Button
        size="sm"
        variant="light"
        startContent={<Scaling size={12} />}
        onPress={handleOpenKnownScale}
        className="h-5 min-w-0 px-2 text-[10px] flex-shrink-0"
        style={{ color: 'var(--text-muted)' }}
      >
        More
      </Button>
    </div>
  );
}
